/* Flower Power — behaviour layer for fp-* sections. Dependency-free, progressive. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Reveal-on-scroll + gauge needle sweep */
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 })
    : null;

  const observe = (root = document) => {
    root.querySelectorAll('[data-fp-reveal]:not(.is-visible), .fp-gauge[data-animate="true"]:not(.is-visible)').forEach((el) => {
      if (io && !reduceMotion) io.observe(el);
      else el.classList.add('is-visible');
    });
  };

  /* Count-up numbers: <span data-fp-count="4.4" data-decimals="1"> */
  const countUp = (el) => {
    const target = parseFloat(el.dataset.fpCount);
    const decimals = parseInt(el.dataset.decimals || '0', 10);
    const fmt = (n) => n.toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    if (reduceMotion || Number.isNaN(target)) { el.textContent = fmt(target || 0); return; }
    const start = performance.now();
    const dur = 1400;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      el.textContent = fmt(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  const initCounters = (root = document) => {
    const els = root.querySelectorAll('[data-fp-count]:not([data-counted])');
    if (!els.length) return;
    const cio = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.dataset.counted = 'true';
        countUp(e.target);
        cio.unobserve(e.target);
      });
    }, { threshold: 0.5 });
    els.forEach((el) => cio.observe(el));
  };

  /* Rails: arrow buttons scroll by one viewport of cards */
  const initRails = (root = document) => {
    root.querySelectorAll('[data-fp-rail-root]').forEach((wrap) => {
      if (wrap.dataset.fpBound) return;
      wrap.dataset.fpBound = 'true';
      const prev = wrap.querySelector('[data-fp-rail-prev]');
      const next = wrap.querySelector('[data-fp-rail-next]');
      const activeRail = () => wrap.querySelector('.fp-rail:not([hidden])');
      const update = () => {
        const rail = activeRail();
        if (!rail || !prev || !next) return;
        prev.disabled = rail.scrollLeft < 8;
        next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 8;
      };
      const go = (dir) => {
        const rail = activeRail();
        if (rail) rail.scrollBy({ left: dir * rail.clientWidth * 0.9, behavior: reduceMotion ? 'auto' : 'smooth' });
      };
      prev?.addEventListener('click', () => go(-1));
      next?.addEventListener('click', () => go(1));
      wrap.querySelectorAll('.fp-rail').forEach((r) => r.addEventListener('scroll', update, { passive: true }));
      wrap.addEventListener('fp:tab', update);
      update();
    });
  };

  /* Tabs: [data-fp-tabs] > [role=tab][aria-controls] */
  const initTabs = (root = document) => {
    root.querySelectorAll('[data-fp-tabs]').forEach((list) => {
      if (list.dataset.fpBound) return;
      list.dataset.fpBound = 'true';
      const tabs = [...list.querySelectorAll('[role="tab"]')];
      const select = (tab) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on);
          t.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
        observe(list.closest('section') || document);
        list.closest('[data-fp-rail-root]')?.dispatchEvent(new Event('fp:tab'));
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          const n = tabs[(i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
          n.focus();
          select(n);
        });
      });
    });
  };

  /* Delivery ETA: "Bestelle in 2 Std. 14 Min. – Lieferung Mi., 22.10." (Mon–Fri shipping) */
  const initEta = (root = document) => {
    root.querySelectorAll('[data-fp-eta]').forEach((el) => {
      if (el.dataset.fpBound) return;
      el.dataset.fpBound = 'true';
      const sameDay = el.dataset.sameDay === 'true';
      const cutoff = parseInt(el.dataset.cutoff || '14', 10);
      const min = parseInt(el.dataset.min || '1', 10);
      const max = parseInt(el.dataset.max || '2', 10);
      const out = el.querySelector('[data-fp-eta-text]');
      const isWorkday = (d) => d.getDay() !== 0 && d.getDay() !== 6;
      const addWorkdays = (d, n) => {
        const r = new Date(d);
        while (n > 0) { r.setDate(r.getDate() + 1); if (isWorkday(r)) n -= 1; }
        return r;
      };
      const fmt = (d) => d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });

      const render = () => {
        const now = new Date();
        let ship = new Date(now);
        const shipsToday = sameDay && isWorkday(now) && now.getHours() < cutoff;
        if (sameDay && !shipsToday) {
          ship.setDate(ship.getDate() + 1);
          while (!isWorkday(ship)) ship.setDate(ship.getDate() + 1);
        }
        const from = addWorkdays(ship, min);
        const to = addWorkdays(ship, max);
        const range = min === max ? `<b>${fmt(from)}</b>` : `<b>${fmt(from)} – ${fmt(to)}</b>`;

        if (shipsToday) {
          const end = new Date(now); end.setHours(cutoff, 0, 0, 0);
          const mins = Math.max(1, Math.round((end - now) / 60000));
          const h = Math.floor(mins / 60);
          const m = mins % 60;
          const left = h > 0 ? `${h} Std. ${m} Min.` : `${m} Min.`;
          out.innerHTML = `Bestelle in <b>${left}</b> – wir versenden noch heute. Ankunft ${range}`;
        } else {
          out.innerHTML = `Jetzt bestellen – voraussichtlich bei dir ${range}`;
        }
        el.hidden = false;
      };
      render();
      setInterval(render, 30000);
    });
  };

  /* Power-Level finder */
  const initFinder = (root = document) => {
    root.querySelectorAll('[data-fp-finder]').forEach((finder) => {
      if (finder.dataset.fpBound) return;
      finder.dataset.fpBound = 'true';
      const input = finder.querySelector('input[type="range"]');
      const items = [...finder.querySelectorAll('[data-fp-level]')];
      const gauge = finder.querySelector('.fp-finder__gauge .fp-gauge');
      const moodEls = finder.querySelectorAll('[data-fp-mood]');
      const valueEl = finder.querySelector('[data-fp-finder-value]');
      const countEl = finder.querySelector('[data-fp-finder-count]');
      const empty = finder.querySelector('[data-fp-finder-empty]');
      const rail = finder.querySelector('.fp-rail');

      const apply = () => {
        const v = parseFloat(input.value);
        gauge?.style.setProperty('--level', v);
        finder.style.setProperty('--fill', `${((v - 1) / 4) * 100}%`);
        if (valueEl) valueEl.textContent = String(v).replace('.', ',');
        moodEls.forEach((m) => { m.hidden = parseInt(m.dataset.fpMood, 10) !== Math.round(v); });
        input.setAttribute('aria-valuetext', `Power Level ${v} von 5`);
        let shown = 0;
        items.forEach((item) => {
          const lvl = parseFloat(item.dataset.fpLevel);
          const match = Math.abs(lvl - v) <= 0.5;
          item.hidden = !match;
          if (match) { shown += 1; item.style.setProperty('--i', shown); }
        });
        if (countEl) countEl.textContent = shown;
        if (empty) empty.hidden = shown > 0;
        if (rail) rail.scrollLeft = 0;
        finder.querySelector('[data-fp-rail-root]')?.dispatchEvent(new Event('fp:tab'));
      };
      input.addEventListener('input', apply);
      finder.querySelectorAll('[data-fp-set]').forEach((b) =>
        b.addEventListener('click', () => { input.value = b.dataset.fpSet; apply(); })
      );
      apply();
    });
  };

  /* Pointer parallax for hero stickers */
  const initParallax = (root = document) => {
    if (reduceMotion || !window.matchMedia('(hover: hover)').matches) return;
    root.querySelectorAll('[data-fp-parallax-root]').forEach((scene) => {
      if (scene.dataset.fpBound) return;
      scene.dataset.fpBound = 'true';
      const layers = scene.querySelectorAll('[data-fp-depth]');
      let raf = 0;
      scene.addEventListener('pointermove', (e) => {
        const r = scene.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          layers.forEach((l) => {
            const d = parseFloat(l.dataset.fpDepth);
            l.style.translate = `${x * d}px ${y * d}px`;
          });
        });
      });
      scene.addEventListener('pointerleave', () => layers.forEach((l) => { l.style.translate = '0 0'; }));
    });
  };

  /* Offer: gram pills update the big price; countdown runs to one fixed end date for everyone */
  const initOffer = (root = document) => {
    root.querySelectorAll('[data-fp-offer]').forEach((offer) => {
      if (offer.dataset.fpBound) return;
      offer.dataset.fpBound = 'true';
      const $ = (sel) => offer.querySelector(sel);
      const burst = $('[data-fp-offer-burst]');

      const paint = () => {
        const v = offer.querySelector('input[name="id"]:checked');
        if (!v) return;
        $('[data-fp-offer-now]').textContent = v.dataset.price;
        $('[data-fp-offer-was]').textContent = v.dataset.compare;
        $('[data-fp-offer-save]').textContent = v.dataset.save;
        $('[data-fp-offer-unit]').textContent = v.dataset.unit;
        const pct = parseInt(v.dataset.pct, 10) || 0;
        $('[data-fp-offer-pct]').textContent = `−${pct}%`;
        burst.hidden = pct < 5;
        offer.querySelector('.fp-offer__save').hidden = pct < 1;
        offer.querySelector('.fp-offer__was').hidden = pct < 1;
      };
      offer.querySelectorAll('input[name="id"]').forEach((i) => i.addEventListener('change', paint));
      paint();

      const timer = $('[data-fp-offer-timer]');
      const end = Date.parse(offer.dataset.end || '');
      if (!timer || Number.isNaN(end)) return;
      const units = { d: timer.querySelector('[data-unit="d"]'), h: timer.querySelector('[data-unit="h"]'), m: timer.querySelector('[data-unit="m"]'), s: timer.querySelector('[data-unit="s"]') };
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        const left = Math.floor((end - Date.now()) / 1000);
        if (left <= 0) { timer.hidden = true; clearInterval(iv); return; }
        units.d.textContent = Math.floor(left / 86400);
        units.h.textContent = pad(Math.floor((left % 86400) / 3600));
        units.m.textContent = pad(Math.floor((left % 3600) / 60));
        units.s.textContent = pad(left % 60);
        timer.hidden = false;
      };
      const iv = setInterval(tick, 1000);
      tick();
    });
  };

  /* "Ultimate Trust Badges" app renders a same-origin srcdoc iframe with hard-coded dark,
     centred text. On dark fp sections it becomes invisible, so match colour + alignment. */
  const styleTrustBadges = () => {
    document.querySelectorAll('iframe#trust-bar').forEach((frame) => {
      const dark = frame.closest('.fp-section[data-bg="ink"], .fp-section[data-bg="green"]');
      const inOffer = frame.closest('.fp-offer');
      if (!dark && !inOffer) return;
      const apply = () => {
        const doc = frame.contentDocument;
        if (!doc || !doc.head || doc.getElementById('fp-trust-style')) return;
        const align = frame.closest('.fp-offer') ? 'left' : 'center';
        const style = doc.createElement('style');
        style.id = 'fp-trust-style';
        style.textContent = `
          .trustBarContainer, .trustBarTitle, .badgesContainer { text-align: ${align} !important; }
          ${dark ? '.trustBarTitle { color: #fff4e0 !important; }' : ''}
          .trustBarTitle { font-family: 'FP Grotesque', Arial, sans-serif !important; font-weight: 700 !important; font-size: 15px !important; opacity: .85; margin-bottom: 8px !important; }
          .badgeContainer { width: 64px !important; height: 44px !important; margin-right: 8px; }`;
        doc.head.appendChild(style);
      };
      apply();
      frame.addEventListener('load', apply);
    });
  };

  /* ---------- Long collection SEO text: clamp + "Weiterlesen" ---------- */
  const initClamp = (root = document) => {
    root.querySelectorAll('[data-fp-clamp]:not([data-fp-ready])').forEach((box) => {
      box.dataset.fpReady = '';
      const text = box.querySelector('.fp-clamp__text');
      const btn = box.querySelector('.fp-clamp__more');
      if (!text || !btn || text.scrollHeight <= text.clientHeight + 4) return;
      box.classList.add('is-clamped');
      btn.hidden = false;
      btn.addEventListener('click', () => {
        const open = box.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open);
        btn.textContent = open ? 'Weniger anzeigen' : 'Weiterlesen';
        if (!open) box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    });
  };

  const init = (root = document) => {
    styleTrustBadges();
    initClamp(root);
    initOffer(root);
    observe(root);
    initCounters(root);
    initRails(root);
    initTabs(root);
    initEta(root);
    initFinder(root);
    initParallax(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  /* Theme editor support */
  document.addEventListener('shopify:section:load', (e) => init(e.target));

  /* Cards injected by AJAX filtering / quick-add / recommendations */
  let pending = 0;
  new MutationObserver(() => {
    cancelAnimationFrame(pending);
    pending = requestAnimationFrame(() => { observe(); styleTrustBadges(); });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
